'use client';

import { useEffect, useState } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme, type Theme } from '@/lib/useTheme';

const ORDER: Theme[] = ['light', 'dark', 'system'];
const ICON: Record<Theme, typeof Sun> = { light: Sun, dark: Moon, system: Monitor };
const LABEL: Record<Theme, string> = { light: 'Light', dark: 'Dark', system: 'System' };

/**
 * Cycles light → dark → system. Renders the light icon until mounted so the
 * server/client first paint match (theme state hydrates in an effect).
 */
export default function ThemeToggle({ className = '' }: { className?: string }) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const current: Theme = mounted ? theme : 'light';
  const Icon = ICON[current];
  const next = ORDER[(ORDER.indexOf(current) + 1) % ORDER.length];

  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      aria-label={`Theme: ${LABEL[current]}. Activate to switch to ${LABEL[next]}.`}
      title={`Theme: ${LABEL[current]} (click for ${LABEL[next]})`}
      className={`min-h-[40px] min-w-[40px] inline-flex items-center justify-center rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700 transition-colors ${className}`}
    >
      <Icon size={20} aria-hidden="true" />
      <span className="sr-only">Toggle color theme</span>
    </button>
  );
}
